<template>
  <main role="main" class="py-4">
    <b-container class="container-x" style="max-width: 800px;">
      <!-- Title Section -->
      <div class="d-flex flex-row align-items-center justify-content-between mb-4 border-bottom pb-2">
        <h4 class="m-0 text-dark font-weight-bold">{{ $t("smartVoiceTokenSystem") }}</h4>
      </div>

      <!-- Config Card -->
      <b-card class="shadow-sm border">
        <div v-if="isLoading" class="py-5 text-center">
          <b-spinner variant="primary"></b-spinner>
          <div class="text-muted mt-2 small">{{ $t("loadingData") }}</div>
        </div>

        <b-form v-if="!isLoading" @submit.prevent="submitForm">
          <!-- Credentials Section -->
          <div class="mb-4">
            <h6 class="text-primary font-weight-bold mb-3">{{ $t("authenticationConfig") }}</h6>
            
            <b-form-group :label="$t('authorizationLabel')" label-for="input-auth" label-class="font-weight-bold text-secondary small">
              <b-form-input id="input-auth" type="text" v-model="authorization" placeholder="Bearer..." required />
            </b-form-group>

            <b-form-row>
              <b-form-group class="col-md-6" :label="$t('tokenIdLabel')" label-for="input-token-id" label-class="font-weight-bold text-secondary small">
                <b-form-input id="input-token-id" type="text" v-model="tokenId" placeholder="Token ID" required />
              </b-form-group>

              <b-form-group class="col-md-6" :label="$t('tokenKeyLabel')" label-for="input-token-key" label-class="font-weight-bold text-secondary small">
                <b-form-input id="input-token-key" type="text" v-model="tokenKey" placeholder="Token Key" required />
              </b-form-group>
            </b-form-row>
          </div>

          <!-- Connection Section -->
          <div class="mb-4 border-top pt-3">
            <h6 class="text-primary font-weight-bold mb-3">{{ $t("connectionConfig") }}</h6>
            
            <b-form-row>
              <b-form-group class="col-md-8" :label="$t('domainGrpc')" label-for="input-domain" label-class="font-weight-bold text-secondary small">
                <b-form-input id="input-domain" type="text" v-model="domain" placeholder="example.com" required />
              </b-form-group>

              <b-form-group class="col-md-4" :label="$t('portGrpc')" label-for="input-port" label-class="font-weight-bold text-secondary small">
                <b-form-input id="input-port" type="text" v-model="port" placeholder="50051" required />
              </b-form-group>
            </b-form-row>
          </div>

          <!-- Footer Area -->
          <div class="d-flex flex-column flex-sm-row align-items-center justify-content-between border-top pt-3 mt-4">
            <div class="text-muted small mb-3 mb-sm-0">
              <span v-if="modifiedAt">
                {{ $t("updateDate") }}: <strong>{{ formatDate(modifiedAt) }}</strong>
              </span>
            </div>
            
            <b-button type="submit" variant="primary" :disabled="isSubmitting" style="min-width: 140px;">
              <b-spinner v-if="isSubmitting" small class="mr-1"></b-spinner>
              {{ $t("saveConfigToken") }}
            </b-button>
          </div>
        </b-form>
      </b-card>
    </b-container>
  </main>
</template>

<style lang="scss" scoped>
.container-x {
  max-width: 800px;
}
.card {
  border-radius: 8px;
  background-color: #f4f9ff;
}
.font-weight-bold {
  font-weight: 600 !important;
}
</style>
  
  <script lang="ts">
    import { Component } from 'vue-property-decorator';
    import _ from "lodash";
    import BaseComponent from "@/components/BaseComponent";
    import * as customerService from "@/service/customerService";
    import { SmartVoiceToken } from '@/model/utilModels';
    import { sanitizeObject } from "@/service/util";
    
    @Component
    export default class ManagerConfigToken extends BaseComponent {
  
      public isChanged = false;
      public isSubmitting = false;
      public isLoading = false;

      public lockedOut = true;
      public authorization = "";
      public tokenId = "";
      public tokenKey = "";
      public domain = "";
      public port = "";

      public id: number | null = null;
      public adminUsers: any[] = [];
      public createdAt: Date | null = null;
      public modifiedAt: Date | null = null;

      mounted() {

        this.fetchCustomer();
        
      }

      private async fetchCustomer() {
        this.isLoading = true;
        try {
          const tokenInfo = await customerService.getSmartVoiceTokenSystem() as SmartVoiceToken;
          if (tokenInfo) {
            this.mapTokenInfo(tokenInfo);
          }
        } catch (e) {
          this.toastErrorOldType(e);
        } finally {
          this.isLoading = false;
        }
      }

      private async submitForm() {
        this.isLoading = true;
        try {
      
          const updatedData = sanitizeObject<SmartVoiceToken>({
            authorization: this.authorization,
            tokenId: this.tokenId,
            tokenKey: this.tokenKey,
            domainGRPC: this.domain,
            portGRPC: this.port,
          });

          const res = await customerService.updateSmartVoiceTokenSystem(updatedData);
          if (res?.data) {
            this.mapTokenInfo(res.data);
          }

          this.toastSuccessDelay(this.$t("updateSmartVoiceSuccessful").toString());
        } catch (error) {
          this.toastErrorOldType(error);
        } finally {
          this.isLoading = false;
        }
      }

      private mapTokenInfo(tokenInfo: SmartVoiceToken) {
        this.authorization = (tokenInfo.authorization || "").trim();
        this.tokenId = (tokenInfo.tokenId || "").trim();
        this.tokenKey = (tokenInfo.tokenKey || "").trim();
        this.domain = (tokenInfo.domainGRPC || "").trim();
        this.port = (tokenInfo.portGRPC || "").trim();
        this.modifiedAt = tokenInfo.modifiedAt;
      }

      public formatDate(dateStr: string | Date | null): string {
        if (!dateStr) return "-";
        const date = new Date(dateStr);
        return new Intl.DateTimeFormat("vi-VN", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        }).format(date);
      }
    }
  </script>
  